import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetInvoicesPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/invoices/models/inputs/get-invoices-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetInvoicesPaginatedQuery } from '@service/cpms-bo-invoice.service';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { InvoiceListFilterSearchController } from '../invoice-list-filter-search/invoice-list-filter-search.controller';
import { InvoiceListFilterStateController } from '../invoice-list-filter-state/invoice-list-filter-state.controller';
import { InvoiceListFilterController } from '../invoice-list-filter/invoice-list-filter.controller';
import { InvoiceListOptionsController } from '../invoice-list-options/invoice-list-options.controller';
import {
  InvoiceListContextProvider,
  useInvoiceListContext,
} from '../invoice.context';
import {
  INVOICE_LIST_FILTER_NAME_LIST,
  INVOICE_LIST_SORT_FIELD,
} from './invoice-list.config';
import * as css from './invoice-list.module.scss';
import { InvoiceListView } from './invoice-list.view';

export const InvoiceListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useInvoiceListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetInvoicesPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // TODO: Should be moved in filter-search component
  //   if (debouncedTextToSearch && debouncedTextToSearch.trim().length > 0) {
  //     params.textToSearch = debouncedTextToSearch.trim() || '';
  //   }

  // TODO: Should be move in dedicated component
  const filterNameActiveList = INVOICE_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filerActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? INVOICE_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetInvoicesPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: InvoiceListViewProtected, status: resourceStatus } =
    withProtectedResource(InvoiceListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.INVOICES,
          action: ActionsByResource.INVOICES.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id: getPaginatedQueryParameters.sortField ?? INVOICE_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <InvoiceListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <InvoiceListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <InvoiceListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filerActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filerActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>{InvoiceListViewProtected}</Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Dialog
          open={actionFilterIsDialogOpen}
          onClose={() => setActionFilterIsDialogOpen(false)}
          position={DialogPosition.RIGHT}>
          <Dialog.Header
            icon="filter-funnel-01"
            title={t('$.invoice-list-filter.title')}
            onClose={() => setActionFilterIsDialogOpen(false)}
          />
          <Dialog.Content>
            <InvoiceListFilterController
              onCancel={() => setActionFilterIsDialogOpen(false)}
              onSuccess={() => setActionFilterIsDialogOpen(false)}
            />
          </Dialog.Content>
        </Dialog>
      )}
    </Stack>
  );
};

export const InvoiceListControllerWithContext = (): React.JSX.Element => {
  return (
    <InvoiceListContextProvider>
      <InvoiceListController />
    </InvoiceListContextProvider>
  );
};
